<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      給与管理
    </Header>

    <div v-show="initialized" class="salary pb-4">
      <!-- 給与か入出金履歴があった場合 -->
      <div v-if="!isNoData">
        <div v-if="balance" class="px-3 pt-3 pb-4">
          <BgWaveCard class="section1">
            <div>
              <p class="text-mild text-size-14 text-weight-500">給与残高</p>
              <h2 class="text-size-32 text-weight-600">
                ¥ {{ currency(balance.amount) }}
              </h2>
              <p
                v-if="balance.salary > 0 && balance.scheduledSalaryTransferDate"
                class="text-mild text-size-14"
                data-testid="scheduledSalaryLabel"
              >
                {{ toMonthDay(balance.scheduledSalaryTransferDate) }}
                振込予定額 ¥ {{ currency(balance.salary) }}
              </p>
            </div>
            <div class="d-flex justify-content-between mt-3">
              <AppButton
                :to="{ name: 'SetBank' }"
                variant="light"
                size="sm"
                class="text-primary-dark text-weight-500 bg-gray-02 mr-2 round-4"
                >振込先口座を変更</AppButton
              >
              <AppButton
                :to="{ name: 'Withdrawal' }"
                variant="success"
                size="sm"
                class="text-weight-500 ml-2 round-4"
                data-testid="withdrawalButton"
                >早期引出</AppButton
              >
            </div>
            <p class="text-mild text-size-14 mt-3">
              早期引出でエラーが発生した場合、営業日（土日祝を除く平日9:00〜18:00）で対応させていただきます。
            </p>
          </BgWaveCard>

          <div
            v-if="balance"
            class="section2 d-flex justify-content-between mt-2"
          >
            <div
              class="flex-grow-1 d-flex align-items-center bg-white border border-outline-color mr-2 round-8"
            >
              <DownloadBadgeBlue class="left"></DownloadBadgeBlue>
              <div class="right">
                <p class="text-mild text-size-12">累計収入</p>
                <p class="text-size-14 text-weight-600">
                  ¥ {{ currency(balance.totalIncome) }}
                </p>
              </div>
            </div>
            <div
              class="flex-grow-1 d-flex align-items-center bg-white border border-outline-color round-8"
            >
              <UploadBadgeGreen class="left"></UploadBadgeGreen>
              <div class="right">
                <p class="text-mild text-size-12">累計引出</p>
                <p class="text-size-14 text-weight-600">
                  ¥ {{ currency(balance.totalWithdrawal) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="section3">
          <h3 class="text-mild text-size-14 text-weight-500 px-3 pb-2">
            入出金履歴
          </h3>
          <ul class="history-list border-top border-outline-color">
            <li
              v-for="history in balanceHistories"
              :key="history.id"
              class="bg-white border-bottom border-outline-color"
            >
              <div class="d-block p-3" @click="onClickHistory(history)">
                <div class="d-flex justify-content-between align-items-center">
                  <p class="text-size-14 text-weight-500">
                    {{ toYearMonthDay(history.createdAt) }}

                    <BBadge
                      pill
                      :variant="statusLabelVariant(history)"
                      class="flex-shrink-0 text-weight-400 ml-3 mt-1"
                      >{{ statusLabel(history) }}</BBadge
                    >
                  </p>
                  <div class="d-flex align-items-center">
                    <p :class="getHistoryNumClass(history)">
                      {{ getTransactionSymbol(history) }}
                      ¥ {{ currency(calcTotal(history)) }}
                    </p>
                    <div class="arrow-area text-center">
                      <FeatherIcon
                        icon="chevron-right"
                        size="1x"
                        class="text-gray-05 ml-1"
                      />
                    </div>
                  </div>
                </div>
                <div class="mt-1">
                  <p class="text-gray-05 text-size-12">
                    {{ getTransactionText(history) }}
                  </p>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- 給与も入出金履歴も無かった場合 -->
      <div v-else class="px-3">
        <div class="text-center pt-5">
          <NoSalary />
          <p class="text-black text-size-18 text-weight-500">
            仕事をして<br />給与を受け取りましょう！
          </p>
        </div>
        <div class="no-data-text">
          <p class="text-mild text-size-14">現在確認できる給与はありません</p>
        </div>
        <AppButtonOutline
          :to="{ name: 'SetBank' }"
          :block="true"
          class="text-primary-dark text-weight-500"
          >振込先口座を変更</AppButtonOutline
        >
        <AppButtonOutline
          :to="{ name: 'Mypage' }"
          :block="true"
          class="text-primary-dark text-weight-500 mt-3"
          >戻る</AppButtonOutline
        >
      </div>
    </div>

    <BModal
      v-model="isShowTransferModal"
      title="給与明細"
      hide-footer
      centered
      class="history-modal"
    >
      <div v-if="targetHistory">
        <div class="d-flex justify-content-between">
          <p class="text-mild text-size-14">報酬</p>
          <p class="text-mild text-size-14">
            ¥
            {{ currency(calcReward(targetHistory)) }}
          </p>
        </div>
        <div class="d-flex justify-content-between mt-1">
          <p class="text-mild text-size-14">交通費</p>
          <p class="text-mild text-size-14">
            ¥ {{ currency(targetHistory.optionValue) }}
          </p>
        </div>
        <div class="d-flex justify-content-between mt-1">
          <p class="text-mild text-size-14">源泉徴収額</p>
          <p class="text-mild text-size-14">
            -&ensp;¥ {{ currency(targetHistory.withholdingTax) }}
          </p>
        </div>
        <div class="d-flex justify-content-between mt-3">
          <p class="text-size-16 text-weight-500">総計</p>
          <p class="text-blue text-size-16 text-weight-500">
            ¥ {{ currency(calcTotal(targetHistory)) }}
          </p>
        </div>
      </div>
      <AppButtonOutline
        block
        class="text-primary-dark text-weight-500 mt-3"
        @click="isShowTransferModal = false"
        >閉じる</AppButtonOutline
      >
    </BModal>
    <BModal
      v-model="isShowDrawerModal"
      title="給与明細"
      hide-footer
      centered
      class="history-modal"
    >
      <div v-if="targetHistory">
        <div class="d-flex justify-content-between">
          <p class="text-mild text-size-14">申請額</p>
          <p class="text-mild text-size-14">
            ¥
            {{
              currency(
                targetHistory.amountExcludeOption + targetHistory.optionValue
              )
            }}
          </p>
        </div>
        <div class="d-flex justify-content-between mt-1">
          <p class="text-mild text-size-14">手数料</p>
          <p class="text-mild text-size-14">
            -&ensp;¥
            {{ currency(targetHistory.optionValue) }}
          </p>
        </div>
        <div class="d-flex justify-content-between mt-3">
          <p class="text-size-16 text-weight-500">振込額</p>
          <p class="text-blue text-size-16 text-weight-500">
            ¥
            {{ currency(targetHistory.amountExcludeOption) }}
          </p>
        </div>
      </div>
      <AppButtonOutline
        block
        class="text-primary-dark text-weight-500 mt-3"
        @click="isShowDrawerModal = false"
        >閉じる</AppButtonOutline
      >
    </BModal>
    <BlockBack />
  </div>
</template>

<script lang="ts">
import Header from "@/components/organisms/common/Header.vue";
import BgWaveCard from "@/components/organisms/common/BgWaveCard.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import AppButtonOutline from "@/components/atoms/button/AppButtonOutline.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import DownloadBadgeBlue from "@/assets/svg/DownloadBadgeBlue.svg?component";
import UploadBadgeGreen from "@/assets/svg/UploadBadgeGreen.svg?component";
import { salaryService } from "@/app/services/SalaryService";
import { accountService } from "@/app/services/AccountService";
import { applicationService } from "@/app/services/ApplicationService";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import { Component, Vue } from "vue-facing-decorator";
import { Balance } from "@/types/balance/Balance";
import { BalanceHistory, SimpleOrder } from "@/types/balance/BalanceHistory";
import { formatNumber } from "@/utils/number";
import {
  formatFromISOString,
  formatFromUnixTimestamp,
  parseISO,
  subMonths
} from "@/utils/date";
import { routerPush } from "@/utils/routerPush";

const FETCH_HISTORY_COUNT = 15;
const THRESHOLD = 50;

@Component({
  components: {
    Header,
    BgWaveCard,
    AppButton,
    AppButtonOutline,
    FeatherIcon,
    DownloadBadgeBlue,
    UploadBadgeGreen,
    BlockBack
  }
})
export default class SalaryIndex extends Vue {
  balance: null | Balance = null;
  balanceHistories: BalanceHistory[] = [];
  // モーダルで表示する履歴
  targetHistory: null | BalanceHistory = null;
  // 振り込み情報モーダル
  isShowTransferModal = false;
  // 引き出し情報モーダル
  isShowDrawerModal = false;
  // データの有無でデザインが変わるため、データ取得完了までの待機用
  initialized = false;
  windowHeight = 0;
  bodyHeight = 0;
  hasNext = false;
  isFetching = false;

  beforeCreate() {
    this.$loading.show();
  }

  async created() {
    // アカウント情報を更新
    await accountService.updateUserData();

    this.balance = await salaryService.fetchBalance();

    await this.fetchBalanceHistories();

    this.initialized = true;
    this.$loading.hide();
  }

  mounted() {
    this.windowHeight = window.outerHeight;
    this.bodyHeight = document.body.offsetHeight;
    window.addEventListener("scroll", this.onScroll);
  }

  toMonthDay(date: string) {
    return formatFromISOString(date, "M月d日");
  }

  toYearMonthDay(date: string) {
    return formatFromISOString(date, "yy/MM/dd");
  }

  currency(value: string | number) {
    return formatNumber(Number(value));
  }

  unmounted() {
    window.removeEventListener("scroll", this.onScroll);
  }

  onClickBack() {
    this.$router.push({ name: "Mypage" });
  }

  async onClickHistory(history: BalanceHistory) {
    this.targetHistory = history;
    // 応募に紐づく前の履歴の場合は内訳画面ではなくモーダルで表示
    if (history.isBeforeApplicationLink) {
      if (history.transactionType !== "withdrawal") {
        this.isShowTransferModal = true;
        return;
      }
      this.isShowDrawerModal = true;
      return;
    }

    if (history.transactionType !== "reward") {
      await routerPush(this, {
        name: "SalaryDetail",
        query: { transferRequestId: history.transferRequest.id }
      });
    }

    this.isShowTransferModal = true;
  }

  // スクロールで下端に近づいたら追加読み込み
  onScroll() {
    if (this.isFetching || !this.hasNext) return;
    const scrollPos = window.scrollY;
    const bottomDistance = this.bodyHeight - this.windowHeight - scrollPos;
    if (bottomDistance < THRESHOLD) {
      this.fetchBalanceHistories();
    }
  }

  getHistoryNumClass(history: BalanceHistory) {
    let className = "text-size-14 text-weight-600";
    if (
      history.transactionType === "withdrawal" ||
      history.transactionType === "salary"
    ) {
      className += " text-green";
    } else {
      className += " text-blue";
    }
    return className;
  }

  getTransactionText(history: BalanceHistory) {
    if (history.transactionType === "withdrawal") {
      return "早期引出";
    } else if (history.transactionType === "salary") {
      const month = formatFromUnixTimestamp(
        subMonths(parseISO(history.createdAt), 1),
        "MM"
      );
      return `定期給与自動引出（${month}月勤務分）`;
    } else if (!history.order) {
      return "";
    } else {
      const s = `${history.order.office.name} ${this.toDisplayOrderTime(
        history.order
      )}`;
      if (history.application) {
        return `${s} ${applicationService.getFormattedWorkTimeRange(
          history.application
        )}`;
      }
      return s;
    }
  }

  toDisplayOrderTime(order: SimpleOrder) {
    return formatFromISOString(order.date, "yy/MM/dd");
  }

  // 引き出しか振込かを見て+-を付与
  getTransactionSymbol(history: BalanceHistory) {
    if (
      history.transactionType === "withdrawal" ||
      history.transactionType === "salary"
    ) {
      return "-";
    }
    return "+";
  }

  calcReward(history: BalanceHistory) {
    return history.amountExcludeOption + history.withholdingTax;
  }

  calcTotal(history: BalanceHistory) {
    return history.amountExcludeOption + history.optionValue;
  }

  statusLabel(history: BalanceHistory) {
    if (!history.transferRequest) return "";

    if (history.transferRequest.status === "requested") {
      return "処理中";
    } else if (history.transferRequest.status === "done") {
      return "処理済";
    } else if (history.transferRequest.status === "error") {
      return history.transferRequest.errorMessage || "不明なエラー";
    } else {
      throw new Error("invalid status");
    }
  }

  statusLabelVariant(history: BalanceHistory) {
    if (!history.transferRequest) return "";

    if (history.transferRequest.status === "requested") {
      return "warning";
    } else if (history.transferRequest.status === "done") {
      return "info";
    } else if (history.transferRequest.status === "error") {
      return "danger";
    } else {
      throw new Error("invalid status");
    }
  }

  async fetchBalanceHistories() {
    this.isFetching = true;

    const response = await salaryService.fetchBalanceHistories({
      limit: FETCH_HISTORY_COUNT,
      offset: this.balanceHistories.length,
      ordering: "-created_at"
    });
    this.balanceHistories = this.balanceHistories.concat(response.results);

    this.hasNext = !!response.next;
    this.$nextTick(() => {
      this.bodyHeight = document.body.offsetHeight;
    });

    this.isFetching = false;
  }

  get isNoData() {
    if (!this.balance || this.balanceHistories.length === 0) {
      return true;
    } else {
      return false;
    }
  }
}
</script>

<style lang="scss" scoped>
.salary {
  .section1 {
    h2 {
      padding: 2px 0;
    }

    .btn {
      width: 148px;
      padding: 8px;
    }
  }

  .section2 {
    & > div {
      padding: 12px 16px;

      .right {
        margin-left: 10px;

        p:last-child {
          margin-top: 3px;
        }
      }
    }
  }

  .section3 {
    .arrow-area {
      width: 18px;
    }
  }
}

.no-data-text {
  padding: 40px 0;
}

:deep(.modal-header) {
  font-weight: 500;
  color: $primary-dark;
}
</style>
